/**
 * 機台外觀（套版第 1 層：品牌識別）。
 *
 * 這台機器是哪一家公司的 —— 名稱、標誌、主視覺、配色、吉祥物開關 —— 由後端依
 * Host 標頭回傳（`GET /kiosk/theme`，公開免認證）。
 *
 * ## 沒設定就 fallback，不是空白
 *
 * 每一項都可能是 null，各有對應的退路：沒有名稱就顯示「歡迎光臨」、沒有標誌就
 * 顯示名稱文字、沒有配色就沿用 index.css 內建那組、吉祥物預設不顯示。
 *
 * 這樣既有的租戶在還沒設定前不會壞掉，新租戶也不會莫名掛上別人的招牌。
 *
 * ## 為什麼要擋第一次繪製，又為什麼要有逾時
 *
 * 不擋的話開機會先閃一次無品牌的畫面再跳成正確招牌，很難看。但機台是無人看管的：
 * API 連不上時若無限期擋著，現場看到的是一片空白，沒有人知道發生什麼事。所以
 * 「設定回來」與「逾時」兩者先到先算，逾時就照 fallback 畫下去 —— 招牌不對總比
 * 白屏好，而且顧客仍然買得到票（買家端點不需要這份設定）。
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { API_BASE } from './api';
import { translate, type Locale } from './i18n';

/** 逾時就照 fallback 畫。機台開機本來就有等待，1.5 秒不會被察覺。 */
const THEME_TIMEOUT_MS = 1500;

export interface KioskTheme {
  brand: Record<Locale, string | null>;
  colors: {
    primary: string | null;
    primary_dark: string | null;
    primary_tint: string | null;
    accent: string | null;
    accent_ink: string | null;
  };
  mascot_enabled: boolean;
  /** 閒置畫面的示範對話，各語系最多 3 句；未設定為空陣列＝不畫泡泡。 */
  bubbles: Record<Locale, string[]>;
  /**
   * 鍵為槽位名（後端 KioskThemeService.SLOTS）；未設定的槽位值為 null，缺鍵也是
   * 未設定。刻意不列舉成固定欄位 —— 新增槽位只要改後端與用到它的元件，型別不必
   * 跟著改一輪。
   */
  media: Record<string, string | null>;
}

const EMPTY_THEME: KioskTheme = {
  brand: { 'zh-TW': null, en: null, ja: null },
  colors: {
    primary: null,
    primary_dark: null,
    primary_tint: null,
    accent: null,
    accent_ink: null,
  },
  mascot_enabled: false,
  bubbles: { 'zh-TW': [], en: [], ja: [] },
  media: {},
};

/** CSS 變數名對應 —— 值由後端驗證為 #rrggbb，不可能挾帶其他宣告。 */
const CSS_VARS: Array<[keyof KioskTheme['colors'], string]> = [
  ['primary', '--c-primary'],
  ['primary_dark', '--c-primary-dark'],
  ['primary_tint', '--c-primary-tint'],
  ['accent', '--c-accent'],
  ['accent_ink', '--c-accent-ink'],
];

const ThemeContext = createContext<KioskTheme>(EMPTY_THEME);

export function KioskThemeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [theme, setTheme] = useState<KioskTheme | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  // StrictMode 在 dev 會把 effect 跑兩次；沒有這道就會 setState 兩輪並重打一次 API
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const timer = window.setTimeout(() => setTimedOut(true), THEME_TIMEOUT_MS);
    void (async (): Promise<void> => {
      try {
        const res = await fetch(`${API_BASE}/kiosk/theme`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setTheme((await res.json()) as KioskTheme);
      } catch {
        // 設定拿不到就照 fallback —— 招牌不對仍然能賣票，白屏不能
        setTheme(EMPTY_THEME);
      } finally {
        window.clearTimeout(timer);
      }
    })();
    return () => window.clearTimeout(timer);
  }, []);

  const resolved = theme ?? (timedOut ? EMPTY_THEME : null);

  useEffect(() => {
    if (resolved === null) return;
    const root = document.documentElement;
    for (const [key, cssVar] of CSS_VARS) {
      const value = resolved.colors[key];
      // 沒設定就**移除**覆寫而不是塞空字串，讓 index.css 的預設值重新生效
      if (value === null) root.style.removeProperty(cssVar);
      else root.style.setProperty(cssVar, value);
    }
  }, [resolved]);

  // 還在等（且未逾時）：畫一片底色而不是白屏，開機看起來像啟動中而不是壞掉
  if (resolved === null) {
    return <div style={{ minHeight: '100vh', background: 'var(--c-bg)' }} aria-busy="true" />;
  }
  return <ThemeContext.Provider value={resolved}>{children}</ThemeContext.Provider>;
}

export function useKioskTheme(): KioskTheme {
  return useContext(ThemeContext);
}

/** 目前語系的品牌名稱；該語系沒填就退回繁中，都沒有回 null。 */
export function useBrandName(locale: Locale): string | null {
  const theme = useKioskTheme();
  return useMemo(() => theme.brand[locale] ?? theme.brand['zh-TW'] ?? null, [theme, locale]);
}

/** 歡迎標題 —— 有品牌名就帶入，沒有就用不含公司名的版本。 */
export function useWelcomeHeading(locale: Locale): string {
  const brand = useBrandName(locale);
  return brand === null
    ? translate(locale, 'home.welcomeNoBrand')
    : translate(locale, 'home.welcome', { brand });
}

/**
 * 閒置畫面的示範對話；該語系沒填就退回繁中，都沒填回空陣列（＝不畫泡泡）。
 *
 * 退回繁中而不是退成空的：只填了主語言的租戶，外語旅客看到的仍是這家自己的
 * 航線，比一片空白有用。
 */
export function useAttractBubbles(locale: Locale): string[] {
  const theme = useKioskTheme();
  return useMemo(() => {
    const own = theme.bubbles[locale] ?? [];
    return own.length > 0 ? own : (theme.bubbles['zh-TW'] ?? []);
  }, [theme, locale]);
}

/** 槽位素材的完整位址；未設定（或後端還沒有這個槽位）回 null。 */
export function useThemeMedia(slot: string): string | null {
  const theme = useKioskTheme();
  const path = theme.media[slot];
  return path === undefined || path === null ? null : `${API_BASE}${path}`;
}
